/**
 * "Думаю, как у Claude" — a summary of the model's own thinking, one line per
 * round, above the activity feed of the SAME turn (design doc §10.4).
 *
 * A deliberately separate surface from `ActivityFeed`, not a second section
 * under the same header: the feed's rows are FACTS about what ran, checked by
 * a fixture test; a round summary is the model's own prose about itself,
 * right 41% of the time by Anthropic's own measurement (§10.2.2) — folding it
 * into the feed would lend it the feed's credibility. Same family of markup
 * and the same tokens, so it reads as a sibling, not a stranger.
 *
 * Absent entirely wherever the wire never sent a `reasoning` frame — a legacy
 * `?graph=` build, or `PC_SHOW_REASONING`/`PC_SUMMARY_MODEL` unset — gated on
 * `hasReasoningFrames`, exactly the way `Thread.tsx` gates the feed on
 * `hasFrames` (§5.4/§10.7).
 */

import { useActivity } from './activityStore';
import { formatDuration } from './ActivityRow';
import { cn } from '../lib/cn';

/** §10.5: the block is honestly labelled a draft — this is not decoration,
 *  it is what keeps an unfaithful summary (§10.2.2) from reading as an
 *  explanation. Shown only while expanded, next to the rounds it qualifies. */
const DISCLAIMER = 'Черновые размышления модели. Они не всегда объясняют итоговый ответ.';

function collapsedLabel(ms: number | null): string {
  return ms !== null && ms > 0 ? `Размышления · ${formatDuration(ms)}` : 'Размышления';
}

export default function ReasoningBlock({ turn }: { turn: number }) {
  const feed = useActivity((state) => state.feeds.find((f) => f.turn === turn));
  const toggleReasoning = useActivity((state) => state.toggleReasoning);

  if (!feed || !feed.hasReasoningFrames) return null;
  // Mirrors ActivityFeed's own §5.2 rule: a turn that never produced a round
  // (every one dropped by the Cyrillic/timeout/empty-string fail-open, §10.5)
  // draws nothing once it is over — no empty "Размышления" chip.
  if (feed.finished && feed.reasoning.length === 0) return null;

  const collapsed = feed.finished && feed.reasoningCollapsed;
  const rounds = feed.reasoning;

  const header = collapsed ? (
    <>
      <span className="reasoning-block__caret" aria-hidden="true">
        ▸
      </span>
      <span className="reasoning-block__summary">{collapsedLabel(feed.reasoningMs)}</span>
    </>
  ) : feed.finished ? (
    collapsedLabel(feed.reasoningMs)
  ) : (
    <>
      <span className="reasoning-block__dot" aria-hidden="true" />
      Думаю…
    </>
  );

  return (
    <div className={cn('reasoning-block', collapsed && 'reasoning-block--collapsed')}>
      {feed.finished ? (
        // No `role="status"`/`aria-live` here (review, !265): an explicit
        // role REPLACES the implicit `button` role, so assistive tech would
        // read a status region rather than a widget — `aria-expanded`
        // hanging off a non-widget role, and no way for a screen reader to
        // know this is tappable. The live region has no job here either:
        // once the turn is finished nothing in this header updates again.
        <button
          type="button"
          className="reasoning-block__header reasoning-block__header--button"
          aria-expanded={!collapsed}
          onClick={() => toggleReasoning(turn)}
        >
          {header}
        </button>
      ) : (
        <div className="reasoning-block__header" role="status" aria-live="polite">
          {header}
        </div>
      )}
      <div className="reasoning-block__body">
        <div className="reasoning-block__body-inner">
          {/* `role="log"` only while the turn is still live: its implicit
              `aria-live="polite"`/`aria-atomic="false"` announces each ROUND
              as a whole sentence as it lands — never token by token, since
              what arrives here is already a finished line, not a stream
              (§10.4). Once finished the region is dropped: the header's own
              text change to the collapsed/summary label is the turn's one
              announcement, and a screen reader has no reason to re-visit
              settled rounds under it. */}
          <div className="reasoning-block__rounds" role={feed.finished ? undefined : 'log'} aria-atomic="false">
            {rounds.map((round) => (
              // Plain text, never markdown (§10.4): running the model's own
              // prose through `Markdown.tsx` is a new injection surface for
              // an italic nobody asked for.
              <p key={round.n} className="reasoning-block__round">
                {round.text}
              </p>
            ))}
          </div>
          {!collapsed ? <p className="reasoning-block__disclaimer">{DISCLAIMER}</p> : null}
        </div>
      </div>
    </div>
  );
}
